import type { ThirdPartyFetcher } from '../fetchers';

export const BASE_ENDPOINT = '/api/registry-search';

export const ENDPOINTS = {
  SEARCH_PACKAGES: `${BASE_ENDPOINT}/packages`,
  SEARCH_PACKAGE_VERSIONS: `${BASE_ENDPOINT}/versions`,
} as const;

/**
 * Logical package coordinates on a third-party registry.
 *
 * `qualifiers` are PURL-style ecosystem qualifiers (Linux package fetchers use these for
 * distro / distroVersion / arch); most ecosystems leave them unset.
 */
export type Package = {
  fetcher: ThirdPartyFetcher;
  name: string;
  qualifiers?: Readonly<Record<string, string>>;
};

/** A search hit at the package level — a `Package` plus lightweight display metadata. */
export type PackageHit = Package & {
  description?: string;
  homepage?: string;
};

/**
 * A package version. Stringly-typed because many ecosystems use non-numeric versions
 * (semver pre-release tags, git refs, distro epoch+version strings).
 */
export type Version = {
  value: string;
};

/**
 * What kind of ref a version represents. The default is `'release'` (an ecosystem-native
 * version bump — npm version, maven release, cargo crate version, etc.) and is encoded as an
 * absent `VersionHit.kind` rather than an explicit value, so implementations don't have to
 * set it. The other kinds are meaningful for git fetchers, where a version can be a tag, a
 * branch, or a commit; non-git impls leave `kind` unset.
 *
 * Callers handling all kinds should normalize via `hit.kind ?? 'release'`.
 */
export type VersionKind = 'release' | 'tag' | 'branch' | 'commit';

/** A search hit at the version level — a `Version` plus lightweight display metadata. */
export type VersionHit = Version & {
  /**
   * Upstream-published date for this version, formatted as ISO 8601. Implementations are
   * responsible for parsing and reformatting if the upstream registry returns the date in a
   * different format; callers can rely on the ISO 8601 contract for sort, parse, or display.
   */
  releaseDate?: string;
  kind?: VersionKind;
};

// Wire response shapes for `/api/registry-search/*` endpoints. Shared so both the route's
// `res.json(...)` and the frontend hooks' `get<T>()` calls reference the same contract.
export type SearchPackagesResponse = { results: PackageHit[] };
export type SearchPackageVersionsResponse = { results: VersionHit[] };
